/**
 * KOPS-533: Admin Sponsors Page
 * Manage sponsor accounts — invite, view, configure funding model
 * Same layout pattern as SubscribersPage but simplified for sponsors
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHandshake, faSearch, faPlus, faPaperPlane, faUsers, faBuilding,
  faCheckCircle, faClock, faTimesCircle, faExternalLinkAlt, faChevronDown
} from '@fortawesome/free-solid-svg-icons';
import { colors, fonts, FONT_LINK_HREF } from '../broker-intelligence/shared/colors';
import axiosInstance from '../../helpers/api';

const SPONSOR_TYPES = [
  { value: 'lender', label: 'Lender' },
  { value: 'title', label: 'Title / Escrow' },
  { value: 'brokerage', label: 'Brokerage' },
  { value: 'warranty', label: 'Warranty' },
  { value: 'coach', label: 'Coach / Trainer' },
  { value: 'association', label: 'Association' },
  { value: 'vendor', label: 'Vendor' },
];

const FUNDING_MODELS = [
  { value: 'full_sponsor', label: 'Full Sponsor (100% — sponsor pays all)' },
  { value: 'partial_sponsor', label: 'Partial Sponsor (split cost)' },
  { value: 'referral_only', label: 'Referral Only (agent pays $185/mo)' },
];

interface Sponsor {
  id: string;
  company: string;
  contactName: string;
  email: string;
  phone?: string;
  sponsorType: string;
  fundingModel: string;
  subsidyPct: number;
  seatCount: number;
  agentsInvited: number;
  status: string;
  createdAt: string;
  invitationSentAt?: string;
}

const SponsorsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Sponsor | null>(null);

  // Invite form
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    company: '', contactName: '', email: '', phone: '',
    sponsorType: 'lender', fundingModel: 'full_sponsor', subsidyPct: '100', seatCount: '50', officeShortId: '',
  });
  const [inviting, setInviting] = useState(false);
  const [alert, setAlert] = useState<{ type: string; message: string } | null>(null);

  // Agent invite form (from selected sponsor)
  const [showAgentInvite, setShowAgentInvite] = useState(false);
  const [agentInviteForm, setAgentInviteForm] = useState({ firstName: '', lastName: '', email: '' });
  const [sendingAgentInvite, setSendingAgentInvite] = useState(false);
  const [invitedAgents, setInvitedAgents] = useState<any[]>([]);
  const [importingAgents, setImportingAgents] = useState(false);
  const [scanningAgents, setScanningAgents] = useState(false);
  const [scanResult, setScanResult] = useState<string | null>(null);

  // Fetch sponsors (from brokerage_team_members with sponsor metadata)
  const fetchSponsors = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/lender/all-sponsors');
      if (res.data.success) {
        setSponsors(res.data.sponsors || []);
      }
    } catch (err) {
      console.error('Failed to fetch sponsors:', err);
      // If no dedicated endpoint, show empty with ability to create
      setSponsors([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSponsors(); }, [fetchSponsors]);

  // Load selected from URL — also fetch agents
  useEffect(() => {
    const id = searchParams.get('id');
    if (id && sponsors.length) {
      const found = sponsors.find(s => s.id === id);
      if (found) {
        setSelected(found);
        axiosInstance.get(`/lender/agents?brokerageId=${found.id}`)
          .then(res => { if (res.data.success) setInvitedAgents(res.data.agents || []); })
          .catch(() => setInvitedAgents([]));
      }
    }
  }, [searchParams, sponsors]);

  const selectSponsor = async (s: Sponsor) => {
    setSelected(s);
    setSearchParams({ id: s.id });
    setShowAgentInvite(false);
    // Fetch agents for this sponsor
    try {
      const res = await axiosInstance.get(`/lender/agents?brokerageId=${s.id}`);
      if (res.data.success) setInvitedAgents(res.data.agents || []);
      else setInvitedAgents([]);
    } catch { setInvitedAgents([]); }
  };

  const handleCreateSponsor = async () => {
    if (!inviteForm.email || !inviteForm.company) {
      setAlert({ type: 'danger', message: 'Company name and email are required' });
      return;
    }
    setInviting(true); setAlert(null);
    try {
      // 1. Create sponsor account (Users + Brokerages + Team Member)
      const createRes = await axiosInstance.post('/lender/create-sponsor', {
        company: inviteForm.company,
        contactName: inviteForm.contactName,
        email: inviteForm.email,
        phone: inviteForm.phone,
        sponsorType: inviteForm.sponsorType,
        fundingModel: inviteForm.fundingModel,
        subsidyPct: inviteForm.subsidyPct,
        seatCount: inviteForm.seatCount,
        officeShortId: inviteForm.officeShortId || undefined,
      });

      if (!createRes.data.success) {
        setAlert({ type: 'danger', message: createRes.data.error || 'Failed to create sponsor' });
        setInviting(false);
        return;
      }

      // 2. Send welcome email
      await axiosInstance.post('/lender/send-sponsor-invite', {
        agentEmail: inviteForm.email,
        agentFirstName: inviteForm.contactName.split(' ')[0],
        sponsorCompany: inviteForm.company,
        sponsorType: inviteForm.sponsorType,
        sponsorContactName: inviteForm.contactName,
      });

      const syncMsg = createRes.data.mlsSync ? ` — ${createRes.data.mlsSync.added} agents auto-imported from MLS` : '';
      setAlert({ type: 'success', message: `Sponsor account created + invitation sent to ${inviteForm.email}${syncMsg}` });
      setShowInviteForm(false);
      setInviteForm({ company: '', contactName: '', email: '', phone: '', sponsorType: 'lender', fundingModel: 'full_sponsor', subsidyPct: '100', seatCount: '50', officeShortId: '' });
      fetchSponsors();
    } catch (e: any) {
      setAlert({ type: 'danger', message: e.response?.data?.error || 'Failed to create sponsor' });
    } finally { setInviting(false); }
  };

  const handleSendAgentInvite = async () => {
    if (!selected || !agentInviteForm.email) return;
    setSendingAgentInvite(true);
    try {
      const res = await axiosInstance.post('/lender/send-sponsor-invite', {
        agentEmail: agentInviteForm.email,
        agentFirstName: agentInviteForm.firstName,
        agentLastName: agentInviteForm.lastName,
        sponsorCompany: selected.company,
        sponsorType: selected.sponsorType,
        sponsorContactName: selected.contactName,
      });
      if (res.data.success) {
        setAlert({ type: 'success', message: `Agent invitation sent to ${agentInviteForm.email}` });
        // Add to local list immediately
        setInvitedAgents(prev => [...prev, {
          id: Date.now().toString(),
          name: `${agentInviteForm.firstName} ${agentInviteForm.lastName}`.trim(),
          email: agentInviteForm.email,
          status: 'pending',
          invitedAt: new Date().toISOString(),
        }]);
        setShowAgentInvite(false);
        setAgentInviteForm({ firstName: '', lastName: '', email: '' });
      }
    } catch (e: any) {
      setAlert({ type: 'danger', message: e.response?.data?.error || 'Failed to send agent invite' });
    } finally { setSendingAgentInvite(false); }
  };

  const handleImportAgents = async () => {
    if (!selected) return;
    setImportingAgents(true);
    setAlert(null);
    try {
      const res = await axiosInstance.post('/lender/import-agents', { brokerageId: selected.id });
      if (res.data.success) {
        const { found, added, skipped } = res.data;
        setAlert({ type: 'success', message: `MLS import: ${found} found, ${added} added, ${skipped} already existed` });
        await fetchSponsors();
        // re-pull agents list for the open sponsor
        const agentsRes = await axiosInstance.get(`/lender/agents?brokerageId=${selected.id}`);
        if (agentsRes.data.success) setInvitedAgents(agentsRes.data.agents || []);
      } else {
        setAlert({ type: 'danger', message: res.data.error || 'Import failed' });
      }
    } catch (e: any) {
      setAlert({ type: 'danger', message: e.response?.data?.error || 'Failed to import agents from MLS' });
    } finally { setImportingAgents(false); }
  };

  const filteredSponsors = search
    ? sponsors.filter(s => s.company.toLowerCase().includes(search.toLowerCase()) || s.contactName.toLowerCase().includes(search.toLowerCase()) || s.email.toLowerCase().includes(search.toLowerCase()))
    : sponsors;

  const typeLabel = (t: string) => SPONSOR_TYPES.find(st => st.value === t)?.label || t;
  const fundingLabel = (f: string) => f === 'full_sponsor' ? 'Full Sponsor' : f === 'partial_sponsor' ? 'Partial' : 'Referral';

  return (
    <div style={{ background: colors.navy, minHeight: '100vh', fontFamily: fonts.body }}>
      <link href={FONT_LINK_HREF} rel="stylesheet" />

      {/* Nav */}
      <nav style={{ background: colors.navy3, borderBottom: `2px solid ${colors.gold}`, padding: '12px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <a href="/" style={{ textDecoration: 'none' }}>
            <span style={{ fontFamily: fonts.display, fontSize: 24, letterSpacing: 2, color: colors.white }}>AI<span style={{ color: colors.gold2 }}>Sponsor</span>Portal</span>
          </a>
          <span style={{ color: colors.muted, fontSize: 14 }}>/ Sponsor Management</span>
        </div>
        <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <a href="/admin/subscribers" style={{ color: colors.muted, textDecoration: 'none', fontSize: 13, padding: '6px 12px' }}>Subscribers</a>
          <a href="/admin/sponsors" style={{ color: colors.gold2, textDecoration: 'none', fontSize: 13, padding: '6px 12px', fontWeight: 600 }}>Sponsors</a>
          <a href="/admin/contacts" style={{ color: colors.muted, textDecoration: 'none', fontSize: 13, padding: '6px 12px' }}>Contacts</a>
          <a href="/admin/events" style={{ color: colors.muted, textDecoration: 'none', fontSize: 13, padding: '6px 12px' }}>Events</a>
          <a href="/agent-portal/dashboard" style={{ color: colors.muted, textDecoration: 'none', fontSize: 13, padding: '6px 12px' }}>Dashboard</a>
        </div>
      </nav>

      {/* Stats strip */}
      <div style={{ background: colors.navy2, borderBottom: `1px solid ${colors.navy3}`, padding: '14px 24px', display: 'flex', gap: 24, alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FontAwesomeIcon icon={faHandshake} style={{ color: colors.gold2 }} />
          <span style={{ color: colors.white, fontWeight: 700, fontSize: 18 }}>{sponsors.length}</span>
          <span style={{ color: colors.muted, fontSize: 12 }}>Sponsors</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FontAwesomeIcon icon={faUsers} style={{ color: colors.gold2 }} />
          <span style={{ color: colors.white, fontWeight: 700, fontSize: 18 }}>{sponsors.reduce((sum, s) => sum + s.agentsInvited, 0)}</span>
          <span style={{ color: colors.muted, fontSize: 12 }}>Agents Sponsored</span>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <button onClick={() => setShowInviteForm(!showInviteForm)} style={{
            background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`, border: 'none', borderRadius: 6,
            padding: '8px 16px', fontSize: 12, fontWeight: 700, color: colors.navy, cursor: 'pointer',
          }}>
            <FontAwesomeIcon icon={faPlus} style={{ marginRight: 6 }} /> Create Sponsor Account
          </button>
        </div>
      </div>

      {/* Alert */}
      {alert && (
        <div style={{ padding: '10px 24px', background: alert.type === 'success' ? '#d4edda' : '#f8d7da', color: alert.type === 'success' ? '#155724' : '#721c24', fontSize: 13 }}>
          {alert.message}
          <button onClick={() => setAlert(null)} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}>x</button>
        </div>
      )}

      {/* Create Sponsor Form */}
      {showInviteForm && (
        <div style={{ background: colors.navy2, borderBottom: `1px solid ${colors.navy3}`, padding: '20px 24px' }}>
          <h3 style={{ color: colors.gold2, fontSize: 14, fontWeight: 700, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>
            <FontAwesomeIcon icon={faHandshake} style={{ marginRight: 8 }} /> New Sponsor Account
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
            <input placeholder="Company Name *" value={inviteForm.company} onChange={e => setInviteForm(p => ({ ...p, company: e.target.value }))}
              style={inputStyle} />
            <input placeholder="Contact Name *" value={inviteForm.contactName} onChange={e => setInviteForm(p => ({ ...p, contactName: e.target.value }))}
              style={inputStyle} />
            <input placeholder="Email *" type="email" value={inviteForm.email} onChange={e => setInviteForm(p => ({ ...p, email: e.target.value }))}
              style={inputStyle} />
            <input placeholder="Phone" value={inviteForm.phone} onChange={e => setInviteForm(p => ({ ...p, phone: e.target.value }))}
              style={inputStyle} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
            <select value={inviteForm.sponsorType} onChange={e => setInviteForm(p => ({ ...p, sponsorType: e.target.value }))} style={inputStyle}>
              {SPONSOR_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <select value={inviteForm.fundingModel} onChange={e => setInviteForm(p => ({ ...p, fundingModel: e.target.value }))} style={inputStyle}>
              {FUNDING_MODELS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
            {inviteForm.fundingModel === 'partial_sponsor' && (
              <input placeholder="Subsidy %" type="number" min="1" max="99" value={inviteForm.subsidyPct}
                onChange={e => setInviteForm(p => ({ ...p, subsidyPct: e.target.value }))} style={inputStyle} />
            )}
            <input placeholder="Seat Count" type="number" value={inviteForm.seatCount}
              onChange={e => setInviteForm(p => ({ ...p, seatCount: e.target.value }))} style={inputStyle} />
            <input placeholder="MLS Office ID (auto-populates agents)" value={inviteForm.officeShortId}
              onChange={e => setInviteForm(p => ({ ...p, officeShortId: e.target.value }))} style={inputStyle} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={handleCreateSponsor} disabled={inviting} style={{
              background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`, border: 'none', borderRadius: 6,
              padding: '8px 20px', fontSize: 12, fontWeight: 700, color: colors.navy, cursor: 'pointer',
            }}>
              <FontAwesomeIcon icon={faPaperPlane} style={{ marginRight: 6 }} />
              {inviting ? 'Sending...' : 'Send Sponsor Invitation'}
            </button>
            <button onClick={() => setShowInviteForm(false)} style={{ background: 'transparent', border: `1px solid ${colors.muted}`, borderRadius: 6, padding: '8px 16px', fontSize: 12, color: colors.muted, cursor: 'pointer' }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Main content */}
      <div style={{ display: 'flex', height: 'calc(100vh - 160px)' }}>

        {/* Left panel — sponsor list */}
        <div style={{ width: selected ? '35%' : '100%', borderRight: `1px solid ${colors.navy3}`, display: 'flex', flexDirection: 'column', transition: 'width 0.2s' }}>
          {/* Search */}
          <div style={{ padding: '10px 12px', borderBottom: `1px solid ${colors.navy3}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <FontAwesomeIcon icon={faSearch} style={{ color: colors.muted, fontSize: 12 }} />
              <input placeholder="Search sponsors..." value={search} onChange={e => setSearch(e.target.value)}
                style={{ flex: 1, background: 'transparent', border: 'none', color: colors.white, fontSize: 13, outline: 'none' }} />
            </div>
          </div>

          {/* List */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loading ? (
              <div style={{ padding: 40, textAlign: 'center', color: colors.muted }}>Loading...</div>
            ) : filteredSponsors.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: colors.muted }}>
                <FontAwesomeIcon icon={faHandshake} style={{ fontSize: 32, marginBottom: 12, opacity: 0.3 }} />
                <div>No sponsors yet</div>
                <div style={{ fontSize: 12, marginTop: 8 }}>Click "Create Sponsor Account" to get started</div>
              </div>
            ) : (
              filteredSponsors.map(s => (
                <div key={s.id} onClick={() => selectSponsor(s)} style={{
                  padding: '12px 16px', cursor: 'pointer', borderBottom: `1px solid ${colors.navy3}`,
                  background: selected?.id === s.id ? colors.navy3 : 'transparent',
                }}
                  onMouseEnter={e => { if (selected?.id !== s.id) e.currentTarget.style.background = colors.navy2; }}
                  onMouseLeave={e => { if (selected?.id !== s.id) e.currentTarget.style.background = 'transparent'; }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ color: colors.white, fontWeight: 600, fontSize: 14 }}>{s.company}</div>
                      <div style={{ color: colors.muted, fontSize: 11, marginTop: 2 }}>
                        {s.contactName} — {s.email}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                        background: s.sponsorType === 'lender' ? '#3498db' : s.sponsorType === 'title' ? '#9b59b6' : s.sponsorType === 'coach' ? '#27ae60' : colors.navy3,
                        color: '#fff',
                      }}>{typeLabel(s.sponsorType)}</span>
                      <div style={{ color: colors.muted, fontSize: 10, marginTop: 4 }}>
                        {s.agentsInvited} agents
                        {s.invitationSentAt && <span style={{ marginLeft: 6, color: colors.green }}><FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: 9 }} /> Invited</span>}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Right panel — sponsor detail */}
        {selected && (
          <div style={{ flex: 1, overflowY: 'auto', background: colors.offBg, color: '#333' }}>
            <div style={{ padding: 24 }}>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: 22, color: colors.navy, fontWeight: 700 }}>{selected.company}</h2>
                  <div style={{ color: colors.muted, fontSize: 13, marginTop: 4 }}>{selected.contactName} — {selected.email}</div>
                </div>
                <button onClick={() => { setSelected(null); setSearchParams({}); }}
                  style={{ background: 'none', border: 'none', fontSize: 20, color: colors.muted, cursor: 'pointer' }}>x</button>
              </div>

              {/* Info cards */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, marginBottom: 20 }}>
                <div style={cardStyle}><div style={cardLabel}>Type</div><div style={cardValue}>{typeLabel(selected.sponsorType)}</div></div>
                <div style={cardStyle}><div style={cardLabel}>Funding</div><div style={cardValue}>{fundingLabel(selected.fundingModel)}</div></div>
                <div style={cardStyle}><div style={cardLabel}>Seats</div><div style={cardValue}>{selected.seatCount}</div></div>
                <div style={cardStyle}><div style={cardLabel}>Agents</div><div style={cardValue}>{invitedAgents.length || selected.agentsInvited}</div></div>
                {selected.invitationSentAt && (
                  <div style={cardStyle}><div style={cardLabel}>Invited</div><div style={{ ...cardValue, fontSize: 13, color: colors.green }}>{new Date(selected.invitationSentAt).toLocaleDateString()}</div></div>
                )}
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                <button onClick={() => setShowAgentInvite(!showAgentInvite)} style={{
                  background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`, border: 'none', borderRadius: 6,
                  padding: '8px 16px', fontSize: 12, fontWeight: 700, color: colors.navy, cursor: 'pointer',
                }}>
                  <FontAwesomeIcon icon={faPlus} style={{ marginRight: 6 }} /> Invite Agent
                </button>
                <button onClick={handleImportAgents} disabled={importingAgents} style={{
                  background: colors.navy, border: `1px solid ${colors.gold}`, borderRadius: 6,
                  padding: '8px 16px', fontSize: 12, fontWeight: 700, color: colors.gold2, cursor: 'pointer',
                  opacity: importingAgents ? 0.6 : 1,
                }}>
                  <FontAwesomeIcon icon={faUsers} style={{ marginRight: 6 }} />
                  {importingAgents ? 'Importing...' : 'Import Agents'}
                </button>
                <button onClick={async () => {
                  setScanningAgents(true); setScanResult(null);
                  try {
                    const res = await axiosInstance.post('/sponsor/coaching/scan-all-agents', { brokerageId: selected.id });
                    setScanResult(`Queued: ${res.data.agentCount} agents scanning autonomously`);
                    setAlert({ type: 'success', message: `County scan queued for ${res.data.agentCount} agents` });
                  } catch (err: any) {
                    setScanResult('Scan failed');
                    setAlert({ type: 'danger', message: err?.response?.data?.error || 'Failed to start scan' });
                  } finally { setScanningAgents(false); }
                }} disabled={scanningAgents} style={{
                  background: '#27ae60', border: 'none', borderRadius: 6,
                  padding: '8px 16px', fontSize: 12, fontWeight: 700, color: '#fff', cursor: 'pointer',
                  opacity: scanningAgents ? 0.6 : 1,
                }}>
                  {scanningAgents ? 'Queuing...' : 'Run County Scan'}
                </button>
                <a href={`/sponsor/welcome?company=${encodeURIComponent(selected.company)}&contact=${encodeURIComponent(selected.contactName)}&type=${selected.sponsorType}`}
                  target="_blank" rel="noopener noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', background: colors.navy, color: colors.gold2, border: `1px solid ${colors.gold}`, borderRadius: 6, padding: '8px 14px', fontSize: 11, fontWeight: 600, textDecoration: 'none' }}>
                  <FontAwesomeIcon icon={faExternalLinkAlt} style={{ marginRight: 4 }} /> Preview Welcome Page
                </a>
                {scanResult && <span style={{ fontSize: 11, color: colors.green, fontWeight: 600 }}>{scanResult}</span>}
              </div>

              {/* Agent invite form */}
              {showAgentInvite && (
                <div style={{ background: '#fff', borderRadius: 8, padding: 20, marginBottom: 20, border: `1px solid ${colors.border}` }}>
                  <h4 style={{ fontSize: 13, fontWeight: 700, color: colors.navy, marginBottom: 12 }}>
                    Invite Agent on behalf of {selected.company}
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 12 }}>
                    <input placeholder="First Name" value={agentInviteForm.firstName} onChange={e => setAgentInviteForm(p => ({ ...p, firstName: e.target.value }))}
                      style={{ ...inputStyle, background: '#f8f9fa', color: '#333' }} />
                    <input placeholder="Last Name" value={agentInviteForm.lastName} onChange={e => setAgentInviteForm(p => ({ ...p, lastName: e.target.value }))}
                      style={{ ...inputStyle, background: '#f8f9fa', color: '#333' }} />
                    <input placeholder="Email *" type="email" value={agentInviteForm.email} onChange={e => setAgentInviteForm(p => ({ ...p, email: e.target.value }))}
                      style={{ ...inputStyle, background: '#f8f9fa', color: '#333' }} />
                  </div>
                  <button onClick={handleSendAgentInvite} disabled={sendingAgentInvite || !agentInviteForm.email} style={{
                    background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`, border: 'none', borderRadius: 6,
                    padding: '8px 16px', fontSize: 12, fontWeight: 700, color: colors.navy, cursor: 'pointer',
                  }}>
                    <FontAwesomeIcon icon={faPaperPlane} style={{ marginRight: 6 }} />
                    {sendingAgentInvite ? 'Sending...' : 'Send Agent Invitation'}
                  </button>
                </div>
              )}

              {/* Sponsored Agents list */}
              <div style={{ background: '#fff', borderRadius: 8, padding: 20, marginBottom: 20, border: `1px solid ${colors.border}` }}>
                <h4 style={{ fontSize: 13, fontWeight: 700, color: colors.navy, marginBottom: 12 }}>
                  <FontAwesomeIcon icon={faUsers} style={{ marginRight: 6, color: colors.gold }} />
                  Sponsored Agents ({invitedAgents.length})
                </h4>
                {invitedAgents.length === 0 ? (
                  <p style={{ color: '#999', fontSize: 13, margin: 0 }}>No agents found. Click "Import Agents" to load agents from MLS, or "Invite Agent" to add manually.</p>
                ) : (
                  <div>
                    {invitedAgents.map((agent: any, i: number) => (
                      <div key={agent.id || i} style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '10px 0', borderBottom: i < invitedAgents.length - 1 ? `1px solid ${colors.border}` : 'none',
                      }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>{agent.name || `${agent.firstName || ''} ${agent.lastName || ''}`.trim() || agent.email}</div>
                          <div style={{ fontSize: 11, color: '#888' }}>{agent.email}</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <span style={{
                            fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                            background: agent.status === 'active' ? colors.green : agent.status === 'pending' ? '#f39c12' : colors.muted,
                            color: '#fff',
                          }}>
                            {agent.status === 'active' ? 'Active' : agent.status === 'pending' ? 'Pending' : agent.status || 'Invited'}
                          </span>
                          {agent.invitedAt && (
                            <div style={{ fontSize: 10, color: '#999', marginTop: 2 }}>
                              {new Date(agent.invitedAt).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Sponsor portal link */}
              <div style={{ background: '#fff', borderRadius: 8, padding: 20, border: `1px solid ${colors.border}` }}>
                <h4 style={{ fontSize: 13, fontWeight: 700, color: colors.navy, marginBottom: 8 }}>Sponsor Portal Access</h4>
                <div style={{ fontSize: 13, color: '#555', lineHeight: 1.6 }}>
                  <p>Sponsor logs in at: <a href="https://aisearchportal.com/sponsor" style={{ color: colors.gold }}>aisearchportal.com/sponsor</a></p>
                  <p>Welcome page: <a href={`https://aisearchportal.com/sponsor/welcome?company=${encodeURIComponent(selected.company)}&contact=${encodeURIComponent(selected.contactName)}&type=${selected.sponsorType}`} style={{ color: colors.gold }}>Preview</a></p>
                  {selected.sponsorType === 'lender' && (
                    <p>RESPA Acknowledgment: <a href="https://aisearchportal.com/sponsor/respa-acknowledge" style={{ color: colors.gold }}>View</a></p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ───── Shared styles ───── */
const inputStyle: React.CSSProperties = {
  background: colors.navy3, color: colors.white, border: `1px solid ${colors.navy3}`,
  borderRadius: 6, padding: '8px 12px', fontSize: 13, outline: 'none', width: '100%',
};
const cardStyle: React.CSSProperties = {
  background: '#fff', borderRadius: 8, padding: 14, textAlign: 'center', border: `1px solid ${colors.border}`,
};
const cardLabel: React.CSSProperties = { fontSize: 11, color: colors.muted, marginBottom: 4 };
const cardValue: React.CSSProperties = { fontSize: 18, fontWeight: 700, color: colors.navy };

export default SponsorsPage;
